import { send, apiUrl } from './client';

/**
 * Case photos (M-17) — the gallery a case screen shows and the upload staff
 * make on a patient's behalf.
 *
 * ## `apiUrl` is a real export added by this task, not the plan's guess
 *
 * The plan's sketch assumed `apiUrl` already existed on `client.ts`. It did
 * not: only `send()` and `apiInternals` (a test seam) were exported. Added
 * as a small named export beside them, following `client.ts`'s own
 * `auditLogExportUrl` convention (`${BASE}${path}`) — that helper could not
 * be reused directly because it closes over `BASE` from inside `client.ts`
 * itself, and this module lives outside it.
 *
 * ## `send()` already gets `FormData` right — checked, not assumed
 *
 * `client.ts:346-358` (`request()`) sets `Content-Type` only when the body
 * is NOT `FormData`, deliberately: the browser adds
 * `multipart/form-data; boundary=...` itself, and a hand-set header without
 * that boundary would make the request unparseable server-side. `codesApi
 * .import` (`codes.ts`) already relies on this for its own CSV upload, so
 * `upload` below follows the identical shape rather than reinventing it —
 * no bug to report here, unlike the plan's own uncertainty about it.
 *
 * ## `send<T>()` unwraps `data` the same way for an array as for an object
 *
 * `request()`'s `data: (raw as { data: T }).data` is a plain cast with no
 * branch on `T`'s shape — `list` below is typed `send<CaseMediaRow[]>(...)`
 * and resolves to the array directly, the same as `codesApi`'s non-paginated
 * `availability()` does for its own `readonly CodeAvailabilityRow[]`.
 */

/** The four photo kinds; `StoreCaseMediaRequest::KINDS` on the API. */
/**
 * ONE KIND PER `request.*` SMS TEMPLATE, and that is the rule.
 *
 * This held four. The API ships nine of those templates, each texting a
 * patient for one specific picture, so five of them asked for something
 * neither the patient upload page nor this application could file — a
 * patient sent "please photograph your insurance card" was offered Sensor,
 * Error screen, Receipt and Device serial number. Mirrors
 * `App\Http\Requests\Media\StorePatientMediaRequest::KINDS`.
 */
export type MediaKind =
  | 'sensor_photo'
  | 'error_photo'
  | 'receipt_photo'
  | 'serial_number_photo'
  | 'event_log'
  | 'wrong_sms'
  | 'wasfati'
  | 'insurance_card'
  | 'diabetes_readings';

/**
 * One case photo's metadata, exactly as `CaseMediaResource` renders it —
 * verified field-for-field against
 * `app/Http/Resources/Media/CaseMediaResource.php` in the API repository.
 * Deliberately carries no bytes and no storage path; those come only from
 * `mediaUrl`, an authenticated, audited, scoped download.
 */
export interface CaseMediaRow {
  uuid: string;
  kind: MediaKind;
  mime_type: string;
  size_bytes: number;
  created_at: string;
}

export const mediaApi = {
  /** `GET /cases/{case}/media` — `can:viewAny`, dispensary-scoped, NOT audited. */
  list: (caseUuid: string) => send<CaseMediaRow[]>(`/cases/${caseUuid}/media`),

  /**
   * `POST /cases/{case}/media` — `can:update`. A REAL `multipart/form-data`
   * upload, never a JSON body with a base64 field — see this module's own
   * docblock on why `send()` needs no help here.
   */
  upload: (caseUuid: string, kind: MediaKind, file: File) => {
    const body = new FormData();
    body.set('kind', kind);
    body.set('file', file);

    return send<CaseMediaRow>(`/cases/${caseUuid}/media`, { method: 'POST', body });
  },
};

/**
 * `GET /case-media/{uuid}` — the raw bytes, meant to be used as a plain
 * `<img src>`, never fetched. `can:viewAny`, dispensary-scoped, and AUDITED
 * ON EVERY RETRIEVAL — a gallery of N photos therefore writes N audit rows
 * per render. That is a deliberate, reviewed decision (spec's own audit
 * ruling for this route), not something to work around here with caching
 * or lazy-loading.
 */
export const mediaUrl = (uuid: string) => apiUrl(`/case-media/${uuid}`);
